import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Home } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Paystack redirects customers here with ?reference=... after checkout completes.
 * We verify the transaction (best-effort) and show a friendly confirmation, then
 * route the user back into the main app.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const ref = params.get("reference") || params.get("trxref");
  const [state, setState] = useState("verifying");

  useEffect(() => {
    if (!ref) {
      setState("ready");
      return;
    }
    // Best-effort verification (public endpoint — server checks Paystack and records the tx)
    axios.get(`${API}/payments/verify/${encodeURIComponent(ref)}`)
      .then((r) => {
        // Meta Pixel: fire Purchase once, guarded by reference to avoid dupes on remounts.
        try {
          const seenKey = `fbq_purchase_${ref}`;
          if (typeof window !== "undefined" && typeof window.fbq === "function" && !sessionStorage.getItem(seenKey)) {
            const amt = Number(r?.data?.amount) || 0;
            window.fbq("track", "Purchase", {
              value: amt,
              currency: (r?.data?.currency || "NGN").toUpperCase(),
              content_name: r?.data?.description || "Kobolog payment",
              content_ids: [ref],
            });
            sessionStorage.setItem(seenKey, "1");
          }
        } catch { /* noop */ }
        setState("ready");
      })
      .catch(() => setState("ready")); // success page still shows even if verify times out
  }, [ref]);

  // Auto-bounce to /app/dashboard if authed, /signin otherwise after 5s
  useEffect(() => {
    if (state !== "ready") return;
    const t = setTimeout(() => {
      const authed = !!localStorage.getItem("kobolog_logged_in") || !!localStorage.getItem("kudi_token");
      nav(authed ? "/app/dashboard" : "/", { replace: true });
    }, 5000);
    return () => clearTimeout(t);
  }, [state, nav]);

  return (
    <div className="min-h-dvh bg-gradient-to-br from-kudi-cream via-white to-accent flex items-center justify-center px-4">
      <div className="max-w-sm w-full bg-white border border-border rounded-3xl p-8 shadow-xl text-center" data-testid="payment-success">
        {state === "verifying" ? (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-kudi-green animate-spin mb-4" />
            <h1 className="font-heading text-xl font-bold">Verifying your payment...</h1>
            <p className="text-sm text-kudi-ink/60 mt-1">This takes a few seconds.</p>
          </>
        ) : (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h1 className="font-heading text-2xl font-bold">Payment received! 🎉</h1>
            <p className="text-sm text-kudi-ink/65 mt-2 mb-6">
              The transaction has been logged. Funds will reflect in the trader&apos;s Kobolog wallet within seconds.
            </p>
            <button
              onClick={() => {
                const authed = !!localStorage.getItem("kobolog_logged_in") || !!localStorage.getItem("kudi_token");
                nav(authed ? "/app/dashboard" : "/", { replace: true });
              }}
              className="inline-flex items-center gap-2 bg-kudi-green text-white px-6 py-3 rounded-full font-semibold hover:bg-kudi-green-dark transition"
              data-testid="back-to-app-btn"
            >
              <Home className="w-4 h-4" /> Continue to Kobolog
            </button>
            <p className="text-[11px] text-kudi-ink/45 mt-4">Auto-redirecting in 5 seconds...</p>
          </>
        )}
      </div>
    </div>
  );
}
